import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CATEGORY, KIND, SEVERITY, SEVERITY_ORDER } from './compareFormat';

// The findings of one comparison: what differs from the standard, most serious
// first. Fill-ins and formatting are counted but hidden unless asked for — they are
// expected in every draft and are not marked in the annotated file either.

const Pill = ({ severity }) => {
  const meta = SEVERITY[severity] || SEVERITY.review;
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-solid tw-text-[11px] tw-font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
};
Pill.propTypes = { severity: PropTypes.string };

const Excerpt = ({ label, text, tone }) => (
  <div className="tw-min-w-0">
    <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">{label}</p>
    {text
      ? <p className={`tw-m-0 tw-text-[13px] tw-whitespace-pre-wrap tw-break-words ${tone}`}>{text}</p>
      : <p className="tw-m-0 tw-text-[13px] tw-italic tw-text-slate-400">{label === 'Standard' ? '(not in the standard)' : '(missing from this draft)'}</p>}
  </div>
);
Excerpt.propTypes = { label: PropTypes.string.isRequired, text: PropTypes.string, tone: PropTypes.string };

const FindingCard = ({ finding, onSelect, selected }) => (
  <li id={`finding-${finding.id}`}
    className={`tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 last:tw-border-0 ${selected ? 'tw-bg-primary-50 tw--mx-2 tw-px-2 tw-rounded-lg' : ''}`}>
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <Pill severity={finding.severity} />
      <span className="tw-text-[13px] tw-font-semibold tw-text-slate-900">{finding.title}</span>
      <span className="tw-ml-auto tw-text-[11px] tw-text-slate-400">
        #{finding.id} · {KIND[finding.kind]}{finding.page ? ` · page ${finding.page}` : ''}
        {finding.annotated === false && finding.severity !== 'none' ? ' · not marked in the file' : ''}
      </span>
      {onSelect && finding.annotated && (
        <button type="button" onClick={() => onSelect(finding)}
          className="tw-px-2 tw-py-0.5 tw-rounded tw-bg-transparent tw-border tw-border-solid tw-border-primary-200 tw-text-[11px] tw-font-semibold tw-text-primary-700 hover:tw-bg-primary-50 tw-cursor-pointer">
          Show in file
        </button>
      )}
    </div>
    {(finding.clause || finding.category) && (
      <p className="tw-m-0 tw-mt-0.5 tw-text-[12px] tw-text-slate-500">
        {finding.clause}{finding.clause && finding.category ? ' · ' : ''}{CATEGORY[finding.category]}
      </p>
    )}
    {finding.explanation && <p className="tw-m-0 tw-mt-1 tw-text-[13px] tw-text-slate-700">{finding.explanation}</p>}
    <div className="tw-mt-2 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-p-2 tw-rounded-lg tw-bg-slate-50">
      <Excerpt label="Standard" text={finding.template_text} tone="tw-text-rose-700 tw-line-through tw-decoration-rose-300" />
      <Excerpt label="This draft" text={finding.sales_text} tone="tw-text-emerald-800" />
    </div>
  </li>
);
FindingCard.propTypes = { finding: PropTypes.object.isRequired, onSelect: PropTypes.func, selected: PropTypes.bool };

const FindingList = ({ findings, onSelect, selectedId }) => {
  const [severity, setSeverity] = useState('review_needed');
  const counts = useMemo(() => findings.reduce((acc, f) => ({ ...acc, [f.severity]: (acc[f.severity] || 0) + 1 }), {}), [findings]);
  const needed = findings.filter((f) => f.severity !== 'none');

  const shown = useMemo(() => {
    const list = severity === 'review_needed' ? needed : severity === 'all' ? findings : findings.filter((f) => f.severity === severity);
    return [...list].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || a.id - b.id);
  }, [findings, needed, severity]);

  const filters = [
    { key: 'review_needed', label: 'Needs review', n: needed.length },
    ...SEVERITY_ORDER.filter((s) => counts[s]).map((s) => ({ key: s, label: SEVERITY[s].label, n: counts[s] })),
    { key: 'all', label: 'All', n: findings.length },
  ];

  return (
    <div>
      <div role="group" aria-label="Filter findings" className="tw-flex tw-flex-wrap tw-gap-1 tw-mb-2">
        {filters.map((f) => (
          <button key={f.key} type="button" aria-pressed={severity === f.key} onClick={() => setSeverity(f.key)}
            className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-border tw-border-solid tw-text-[12px] tw-font-semibold tw-cursor-pointer ${
              severity === f.key ? 'tw-bg-slate-900 tw-border-slate-900 tw-text-white' : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50'}`}>
            {f.label} <span className="tw-opacity-70 tw-tabular-nums">{f.n}</span>
          </button>
        ))}
      </div>
      {shown.length === 0
        ? <p className="tw-m-0 tw-py-6 tw-text-center tw-text-sm tw-text-emerald-700">Nothing here — no difference that needs review.</p>
        : <ul className="tw-m-0 tw-p-0 tw-list-none">{shown.map((f) => <FindingCard key={f.id} finding={f} onSelect={onSelect} selected={f.id === selectedId} />)}</ul>}
    </div>
  );
};

FindingList.propTypes = {
  findings: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelect: PropTypes.func,
  selectedId: PropTypes.number,
};

export default FindingList;
